<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col class="d-flex align-center">
          <toggle-input
            v-model="filters.hidePass"
            :is-disabled="isLoading"
            :is-loading="isLoading"
            label="Failed criteria only"
            hide-details
          />
        </v-col>

        <v-spacer />
        <v-col sm="3" class="d-flex align-center">
          <autocomplete-input
            v-model="filters.entity"
            :disabled="isLoading"
            :items="entities"
            :loading="isLoading"
            label="Property"
            class="flex-grow-1"
            hide-details
          />
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row>
        <v-col>
          <h6 class="text-h6">Target: {{ minPartners }} partners per entity</h6>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-text>
              <v-simple-table height="500" dense fixed-header>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Entity</th>
                      <th class="text-left">No. of Partners</th>
                      <th class="text-left">Partners</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in filteredPartnersForEntities"
                      :key="item.entity.key"
                      :class="{
                        'red lighten-4': item.partners.length < minPartners,
                        'green lighten-4': item.partners.length >= minPartners,
                      }"
                    >
                      <td>
                        <span>{{ item.entity.key }}</span>
                        <span class="ml-2 text-caption">({{ item.entity.value }})</span>
                      </td>
                      <td>{{ item.partners.length }}</td>
                      <td style="max-width: 200px">{{ commaSeparatedPartners(item.partners) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-spacer></v-spacer>
        <v-col cols="auto">
          <v-btn :disabled="isLoading" :loading="isLoading" color="primary" depressed @click="onClickExport">
            Export list as CSV
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </page-loader>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import AutocompleteInput from '@/components/forms/autocomplete-input'
import PageLoader from '@/components/page-loader'
import ToggleInput from '@/components/forms/toggle-input'

import PermissionsHandler from '@/mixins/permissions-handler'

import { partners } from '@/services/offers'

import { exportCSV } from '@/utils/csv'

export default {
  name: 'SupportChecker',
  components: {
    AutocompleteInput,
    PageLoader,
    ToggleInput,
  },
  mixins: [PermissionsHandler],
  data: () => ({
    isLoading: false,
    partners: [],
    entities: [
      { key: 'Payment Methods', value: 'paymentMethods' },
      { key: 'Regulations', value: 'regulations', option: 'regulators' },
      { key: 'Trading Platforms', value: 'tradingPlatforms' },
      { key: 'Withdrawal Methods', value: 'withdrawalMethods', option: 'paymentMethods' },
    ],
    minPartners: 4,
    filters: {
      hidePass: true,
      entity: 'paymentMethods',
    },
  }),
  computed: {
    ...mapGetters('options', ['getItemsForOption']),
    partnersForEntities() {
      const entity = this.entities.find(({ value }) => value === this.filters.entity)
      const options = this.getItemsForOption(entity.option || entity.value)
      return options.map(entity => ({
        entity,
        partners: this.partners.filter(this.entitySupported(entity)),
      }))
    },
    filteredPartnersForEntities() {
      if (!this.filters.hidePass) this.partnersForEntities
      return this.partnersForEntities.filter(
        ({ partners }) => !this.filters.hidePass || partners.length < this.minPartners
      )
    },
  },
  async created() {
    await this.getAllPartners()
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getAllPartners() {
      this.isLoading = true

      try {
        const { data } = await partners.getAllPartners({
          compact: true,
          properties: this.entities.map(({ value }) => value),
        })
        this.partners = data
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Partners',
        })
      }

      this.isLoading = false
    },
    commaSeparatedPartners(partners) {
      return partners.map(({ name }) => name).join(', ')
    },
    entitySupported(entity) {
      return partner => (partner[this.filters.entity] || []).includes(entity.value)
    },
    onClickExport() {
      this.isLoading = true

      const headers = ['coin', 'symbol', 'partners', 'slugs'].join(',')
      const body = this.partnersForEntities.map(({ entity, partners }) => {
        const slugs = `"${partners.map(({ slug }) => slug).join(',')}"`
        return [entity.key, entity.value, partners.length, slugs].join(',')
      })

      const csv = [headers].concat(body).join('\n')

      exportCSV(csv, `${this.filters.entity}-export`)

      this.isLoading = false
    },
  },
}
</script>
